// Mot de passe oublié: demande d'un code à 6 chiffres puis nouveau mot de passe.
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword({ email: initialEmail = "", onBack }) {
  const [step, setStep] = useState("request");      // request | verify
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const request = async (e) => {
    e?.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setStep("verify");
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Envoi impossible"); }
    finally { setLoading(false); }
  };

  const reset = async (e) => {
    e?.preventDefault();
    if (pw !== pw2) return setErr("Les deux mots de passe ne sont pas identiques.");
    if (pw.length < 8) return setErr("Le mot de passe doit contenir au moins 8 caractères.");
    setLoading(true); setErr("");
    try {
      await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Mot de passe modifié — connectez-vous.");
      onBack();
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Réinitialisation impossible"); }
    finally { setLoading(false); }
  };

  return (
    <div className="w-full max-w-sm animate-fade-up" data-testid="forgot-password">
      <button onClick={onBack} data-testid="forgot-back"
        className="text-xs text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
      </button>

      <div className="overline mb-2">Mot de passe oublié</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1">
        {step === "request" ? "Recevoir un code" : "Nouveau mot de passe"}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        {step === "request"
          ? "Un code à 6 chiffres vous sera envoyé par courriel."
          : `Entrez le code reçu à ${email} puis choisissez votre nouveau mot de passe.`}
      </p>

      {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
        data-testid="forgot-error">{err}</div>}

      {step === "request" ? (
        <form onSubmit={request} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Courriel</Label>
            <Input type="email" value={email} required autoComplete="email"
              onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MailCheck className="w-4 h-4 mr-1" /> Envoyer le code</>}
          </Button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Code reçu (6 chiffres)</Label>
            <Input inputMode="numeric" maxLength={6} value={code} required
              className="font-mono text-2xl tracking-[0.4em] text-center"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="reset-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Nouveau mot de passe</Label>
            <Input type="password" value={pw} required autoComplete="new-password"
              onChange={(e) => setPw(e.target.value)} data-testid="reset-password" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirmer le mot de passe</Label>
            <Input type="password" value={pw2} required autoComplete="new-password"
              onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-2" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Changer le mot de passe"}
          </Button>
          <button type="button" onClick={request} disabled={loading} data-testid="resend-code"
            className="w-full text-xs text-muted-foreground hover:text-foreground">
            Renvoyer un code
          </button>
        </form>
      )}
    </div>
  );
}
